import Image from 'next/legacy/image';
import { useState } from 'react';

import { MembersOnlyLock } from './MembersOnlyLock';
import Icon from '@components/atoms/icon';
import {
  resolveChipImage,
  resolveChipLabel
} from '@lib/my-team/utils/chip-strategy';
import type { PlannerGameweek } from '@api-service/planner';

type HeaderProps = {
  gameweekData: PlannerGameweek;
  projectedBank?: number | null;
  pendingTransferCount?: number;
  /** Non-paying members see the predicted-points stat padlocked. */
  isPredictedLocked?: boolean;
  /** Non-paying members see the transfers stat padlocked. */
  isTransfersLocked?: boolean;
  /** When set, the bank stat shows a pencil that opens the edit modal. */
  onEditBank?: () => void;
};

const RATING_DECIMAL_TO_PERCENT = 100;

const formatBank = (bank: number) => `£${bank.toFixed(1)}m`;

const formatPredictedPoints = (points: number | null) =>
  points == null ? '-' : `${points.toFixed(1)}pts`;

const formatTeamRating = (rating: number | null) =>
  rating == null ? '-' : `${Math.round(rating * RATING_DECIMAL_TO_PERCENT)}%`;

type TransfersDisplay =
  | { kind: 'unlimited' }
  | { kind: 'count'; value: string }
  | { kind: 'used'; made: number; total: number };

const deriveTransfersDisplay = (
  free: number | null,
  remaining: number | null,
  made: number | undefined,
  pendingTransferCount: number
): TransfersDisplay => {
  if (free == null) return { kind: 'unlimited' };
  if (remaining == null) return { kind: 'count', value: `${free}` };

  // `made` is required by the spec but responses aren't validated at runtime,
  // and an older deployed backend may omit it. Falling back to the remaining
  // allowance keeps a number on screen, but that derivation can never exceed
  // the free allowance — only `made` can show a hit, so it is preferred.
  const derivedFromAllowance = Math.max(0, free - remaining);
  const transfersMade = (made ?? derivedFromAllowance) + pendingTransferCount;

  return { kind: 'used', made: transfersMade, total: free };
};

type StatProps = {
  label: string;
  value: React.ReactNode;
  dataCy: string;
  isHighlighted?: boolean;
  className?: string;
};

const Stat = ({
  label,
  value,
  dataCy,
  isHighlighted,
  className
}: StatProps) => (
  <div
    className={`flex flex-col items-center gap-0.5 px-2 py-2.5 text-center md:py-3 ${
      className ?? ''
    }`}
    data-cy={dataCy}
  >
    <span
      className={`font-sans text-[19px] font-bold leading-[1.2] tabular-nums md:text-[20px] ${
        isHighlighted ? 'text-primary-700' : 'text-neutral-800'
      }`}
    >
      {value}
    </span>
    <span
      className={`font-open-sans text-[10px] font-semibold uppercase tracking-[0.1em] md:text-[11px] ${
        isHighlighted ? 'text-primary-600' : 'text-neutral-600'
      }`}
    >
      {label}
    </span>
  </div>
);

type RatingKey = 'gameweek' | 'rolling';

const FINAL_GAMEWEEK = 38;
const ROLLING_RATING_WINDOW = 5;

/**
 * The rolling rating covers the current gameweek plus the ones after it, so
 * the window shrinks as the season runs out (5GW, then 4GW, 3GW, 2GW). In the
 * final gameweek only the gameweek rating is meaningful.
 */
const deriveRollingWindowSize = (gameweek: number) =>
  Math.max(1, Math.min(ROLLING_RATING_WINDOW, FINAL_GAMEWEEK - gameweek + 1));

const getRatingTabs = (
  rollingWindowSize: number
): {
  key: RatingKey;
  label: string;
  ariaLabel: string;
  dataCy: string;
}[] => [
  {
    key: 'gameweek',
    label: 'GW',
    ariaLabel: 'Gameweek rating',
    dataCy: 'pick-header-gameweek-rating-tab'
  },
  {
    key: 'rolling',
    label: `${rollingWindowSize}GW`,
    ariaLabel: `${rollingWindowSize} gameweek rating`,
    dataCy: 'pick-header-rolling-rating-tab'
  }
];

type RatingStatProps = {
  gameweekRating: string;
  rollingRating: string;
  rollingWindowSize: number;
};

const RatingStat = ({
  gameweekRating,
  rollingRating,
  rollingWindowSize
}: RatingStatProps) => {
  const [selected, setSelected] = useState<RatingKey>('gameweek');

  return (
    <div
      className="flex flex-col items-center gap-1 px-2 py-2.5 text-center md:py-2.5"
      data-cy="pick-header-rating"
    >
      <span
        className="font-sans text-[19px] font-bold leading-[1.2] tabular-nums text-neutral-800 md:text-[20px]"
        data-cy="pick-header-rating-value"
      >
        {selected === 'gameweek' ? gameweekRating : rollingRating}
      </span>
      <div
        className="flex rounded-lg bg-primary-100 p-[3px]"
        role="group"
        aria-label="Rating period"
        data-cy="pick-header-rating-tabs"
      >
        {getRatingTabs(rollingWindowSize).map((tab) => (
          <button
            key={tab.key}
            type="button"
            aria-pressed={selected === tab.key}
            aria-label={tab.ariaLabel}
            onClick={() => setSelected(tab.key)}
            data-cy={tab.dataCy}
            className={`whitespace-nowrap rounded-md px-2 py-1 font-open-sans text-[10px] font-semibold tracking-[0.04em] transition-colors duration-200 md:px-2.5 md:text-[11px] ${
              selected === tab.key
                ? 'bg-neutral-50 text-neutral-800 shadow-sm'
                : 'text-primary-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

type ChipDetails = {
  label: string;
  imageSrc: string | null;
};

const ChipIconCircle = ({
  chip,
  circleSizeClass,
  iconSizePx
}: {
  chip: ChipDetails;
  circleSizeClass: string;
  iconSizePx: number;
}) => {
  if (!chip.imageSrc) return null;
  return (
    <span
      className={`${circleSizeClass} flex flex-shrink-0 items-center justify-center rounded-full bg-white`}
    >
      <Image
        src={chip.imageSrc}
        alt=""
        height={iconSizePx}
        width={iconSizePx}
      />
    </span>
  );
};

const DESKTOP_CHIP_ICON_PX = 18;
const MOBILE_CHIP_ICON_PX = 17;

export const Header = ({
  gameweekData,
  projectedBank,
  pendingTransferCount = 0,
  isPredictedLocked = false,
  isTransfersLocked = false,
  onEditBank
}: HeaderProps) => {
  const { gameweek, ratings, predictedPoints, bank, transfers, activeChip } =
    gameweekData;
  const rollingWindowSize = deriveRollingWindowSize(gameweek);
  const hasProjection = projectedBank != null && projectedBank !== bank;
  const bankValue = hasProjection ? (projectedBank as number) : bank;
  const hasPendingTransfers = pendingTransferCount > 0;

  const transfersDisplay = deriveTransfersDisplay(
    transfers.free,
    transfers.remaining,
    transfers.made,
    pendingTransferCount
  );

  const transfersLabel =
    !isTransfersLocked && transfersDisplay.kind === 'used'
      ? 'Transfers used'
      : 'Transfers';

  const transfersValue = isTransfersLocked ? (
    <MembersOnlyLock
      feature="transfer insights"
      placement="pick_header_transfers"
      dataCy="pick-header-transfers-lock"
    />
  ) : transfersDisplay.kind === 'unlimited' ? (
    <span className="text-primary-600">Unlimited</span>
  ) : transfersDisplay.kind === 'count' ? (
    transfersDisplay.value
  ) : (
    <span>
      {transfersDisplay.made}
      <span className="text-neutral-400">/{transfersDisplay.total}</span>
    </span>
  );

  const chip: ChipDetails | null = activeChip
    ? {
        label: resolveChipLabel(activeChip),
        imageSrc: resolveChipImage(activeChip)
      }
    : null;

  return (
    <div className="border-t border-neutral-200" data-cy="pick-header">
      <div
        className={`grid grid-cols-2 bg-neutral-100 md:grid-cols-4 ${
          chip ? 'md:grid-cols-[1fr_1fr_1fr_1fr_244px]' : ''
        }`}
      >
        {rollingWindowSize > 1 ? (
          <RatingStat
            gameweekRating={formatTeamRating(ratings.current)}
            rollingRating={formatTeamRating(ratings.rolling)}
            rollingWindowSize={rollingWindowSize}
          />
        ) : (
          <Stat
            label="Gameweek rating"
            value={formatTeamRating(ratings.current)}
            dataCy="pick-header-rating"
          />
        )}
        <Stat
          label="Predicted"
          value={
            isPredictedLocked ? (
              <MembersOnlyLock
                feature="predicted points"
                placement="pick_header_predicted"
                dataCy="pick-header-predicted-lock"
              />
            ) : (
              formatPredictedPoints(predictedPoints)
            )
          }
          dataCy="pick-header-predicted-points"
          className="border-l border-neutral-200"
        />
        <Stat
          label={hasProjection ? 'Projected bank' : 'In the bank'}
          value={
            // A projected value is derived from staged transfers, so it
            // cannot be edited directly; the pencil only accompanies the
            // stored amount.
            onEditBank && !hasProjection ? (
              <span className="inline-flex items-center gap-1.5">
                {formatBank(bankValue)}
                <button
                  type="button"
                  aria-label="Edit in the bank amount"
                  onClick={onEditBank}
                  data-cy="pick-header-bank-edit"
                  className="text-neutral-500 transition-colors hover:text-neutral-700"
                >
                  <Icon name="PenFilled" className="text-[14px]" />
                </button>
              </span>
            ) : (
              formatBank(bankValue)
            )
          }
          dataCy="pick-header-bank"
          isHighlighted={hasProjection}
          className="border-t border-neutral-200 md:border-l md:border-t-0"
        />
        <Stat
          label={transfersLabel}
          value={transfersValue}
          dataCy="pick-header-transfers"
          isHighlighted={hasPendingTransfers}
          className="border-l border-t border-neutral-200 md:border-t-0"
        />
        {chip && (
          <div
            className="hidden items-center justify-center border-l border-neutral-200 px-3 py-2.5 md:flex"
            data-cy="pick-header-active-chip"
          >
            <span className="inline-flex items-center gap-2.5 rounded-full bg-primary-50 py-1.5 pl-1.5 pr-3.5 ring-1 ring-inset ring-primary-200">
              <ChipIconCircle
                chip={chip}
                circleSizeClass="h-8 w-8"
                iconSizePx={DESKTOP_CHIP_ICON_PX}
              />
              <span className="flex flex-col">
                <span className="whitespace-nowrap font-open-sans text-sm font-bold leading-[1.3] text-primary-700">
                  {chip.label}
                </span>
                <span className="font-open-sans text-[11px] leading-[1.3] text-neutral-600">
                  Active chip
                </span>
              </span>
            </span>
          </div>
        )}
      </div>
      {chip && (
        <div
          className="flex items-center justify-center gap-2.5 border-t border-neutral-200 bg-primary-50 px-3 py-2.5 md:hidden"
          data-cy="pick-header-active-chip-strip"
        >
          <ChipIconCircle
            chip={chip}
            circleSizeClass="h-[30px] w-[30px]"
            iconSizePx={MOBILE_CHIP_ICON_PX}
          />
          <span className="flex flex-col">
            <span className="font-open-sans text-sm font-bold leading-[1.3] text-primary-700">
              {chip.label}
            </span>
            <span className="font-open-sans text-[11px] leading-[1.3] text-neutral-600">
              Active chip this gameweek
            </span>
          </span>
        </div>
      )}
    </div>
  );
};
